import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useAuth } from "../../auth/AuthContext";
import { fetchUsers } from "../../fetchers/user";
import { fetchAssetHierarchyTree } from "../../fetchers/cmmsAssetHierarchy";
import { deleteWorkOrder, getMyWorkOrders, getWorkOrders } from "../../fetchers/cmmsWorkOrder";
import { getTrades } from "../../fetchers/cmmsTrade";
import { toTradeFilterOptions } from "../cmms/cmmsTradeAssignment";
import { getTradeTags } from "../cmms/cmmsTradeTags";
import AxionControls from "../../components/controls/AxionControls";
import { HideIfNoRole } from "../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmms/cmmsRoles";
import { pushErrorMessage, pushNotification } from "../../utils/notifications";
import cmmsMessages from "../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../cmms/components/CmmsStates";
import messages from "./messages";
import { flattenAssetNodes } from "../cmms/assetHierarchyUtils";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getInputLabelSx,
  getMenuSx,
  getPageHeaderIconSx,
  getPageShellSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getSectionBoxSx,
  getSelectSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
  getSwitchSx,
  getTabsSx,
} from "../cmms/assetHierarchyStyles";
import { filterWorkOrders } from "./workOrderSearch";
import { WorkOrderDateField, filterWorkOrdersByDate, isDateRangeActive } from "./workOrderDateFilter";
import { filterProjectedOccurrences } from "./workOrderWeek";
import { WORK_ORDER_PARAM, parseWorkOrderIdParam } from "./workOrderDeepLink";
import WorkOrderPriorityTile from "./components/WorkOrderPriorityTile";
import WorkOrderTypeTile from "./components/WorkOrderTypeTile";
import WorkOrdersDataGrid from "./components/WorkOrdersDataGrid";
import WorkOrderDateFilter from "./components/WorkOrderDateFilter";
import WorkOrderWeekView from "./components/WorkOrderWeekView";
import WorkOrderMonthView from "./components/WorkOrderMonthView";
import WorkOrderDetailsPanel from "./components/WorkOrderDetailsPanel";
import WorkOrderFormDialog from "./components/WorkOrderFormDialog";
import EscalateDialog from "./components/EscalateDialog";
import {
  getWorkOrderPriorityOptions,
  getWorkOrderStatusName,
  getWorkOrderStatusOptions,
  getWorkOrderTypeOptions,
} from "./workOrderEnums";

const ALL = "ALL";

const WorkOrderView = {
  List: "list",
  Week: "week",
  Month: "month",
};

export default function CmmsWorkOrders() {
  const intl = useIntl();
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  // Remembered, like the grid's columns: someone who plans by the week wants to land on the week.
  const [view, setView] = useLocalStorage("cmmsWorkOrders-view", WorkOrderView.List);
  // Anything stored that is not the week or the month, an old or a hand-edited value included, is
  // the list.
  const isWeekView = view === WorkOrderView.Week;
  const isMonthView = view === WorkOrderView.Month;
  const isListView = !isWeekView && !isMonthView;

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [priorityFilter, setPriorityFilter] = useState(ALL);
  const [tradeFilter, setTradeFilter] = useState(ALL);
  const [mineOnly, setMineOnly] = useState(false);
  const [searchText, setSearchText] = useState("");
  // The list's from–to filter. Not remembered, like the filters above.
  const [dateRange, setDateRange] = useState({ field: WorkOrderDateField.PlannedStart, from: null, to: null });
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [formState, setFormState] = useState({ open: false, workOrderId: null });
  const [escalateState, setEscalateState] = useState({ open: false, workOrderId: null, currentDepartmentId: null, currentTradeId: null });
  const [confirmation, setConfirmation] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Opened from a notification mail, via the ?workOrder=<id> parameter.
   *
   * The drawer fetches by id itself, so this lands on the right work order even when the current
   * filters exclude it: a technician mailed about a breakdown does not first have to work out
   * which dropdown is hiding it.
   *
   * Runs on every change to the parameter rather than once on mount, so a second mail opened in
   * an already-loaded tab moves the drawer instead of doing nothing.
   */
  useEffect(() => {
    const requestedId = parseWorkOrderIdParam(searchParams);
    if (requestedId) setSelectedWorkOrderId(requestedId);
  }, [searchParams]);

  const apiFilters = useMemo(() => {
    const filters = {};
    if (statusFilter !== ALL) filters.status = statusFilter;
    if (typeFilter !== ALL) filters.workOrderType = typeFilter;
    if (priorityFilter !== ALL) filters.priority = priorityFilter;
    if (tradeFilter !== ALL) filters.tradeId = tradeFilter;
    return filters;
  }, [statusFilter, typeFilter, priorityFilter, tradeFilter]);

  /*
   * "Mina arbetsordrar" — the destination the sidebar badge has never had.
   *
   * Whose they are is GetMineAsync's answer, not a filter we could write here: dispatched to me,
   * or assigned to me, or Open and belonging to one of my departments or trades. Hence /mine
   * rather than an assignedResourceId parameter, and hence the label — this is my queue, not what
   * is assigned to me.
   *
   * With no status picked the key is byte for byte the badge's own (useMyWorkOrderCount), so one
   * request serves both and the list cannot show a different number than the badge promised. That
   * also means /mine applies its own default of Open + InProgress, which is what the badge counts.
   * Picking a status sends it through instead, so finished work stays reachable.
   */
  const myStatusNames = statusFilter === ALL ? null : [getWorkOrderStatusName(statusFilter)];
  const myWorkOrdersKey = myStatusNames
    ? ["cmmsMyWorkOrders", companyKey, myStatusNames]
    : ["cmmsMyWorkOrders", companyKey];

  const workOrdersQuery = useQuery(
    mineOnly ? myWorkOrdersKey : ["cmmsWorkOrders", companyKey, apiFilters],
    () => (mineOnly
      ? getMyWorkOrders(companyKey, myStatusNames)
      : getWorkOrders(companyKey, apiFilters)),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  /*
   * The header tiles' own source — the priority counts and the type counts both.
   *
   * Unfiltered on purpose: the four dropdowns above are answered by the API, so the tiles
   * cannot be counted off `workOrdersQuery` without going to zero whenever someone filters to a
   * finished status. Keyed on an empty filter object, which means that with no dropdown set this
   * is byte for byte the list's own key and react-query serves both from one request — the same
   * arrangement MachineWorkOrdersTabLabel relies on. Only while a filter is set does it cost a
   * second request.
   */
  const allWorkOrdersQuery = useQuery(
    ["cmmsWorkOrders", companyKey, {}],
    () => getWorkOrders(companyKey, {}),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: Boolean(companyKey), staleTime: 300000 },
  );

  const usersQuery = useQuery(["users"], () => fetchUsers(), { staleTime: 300000 });

  // Retired trades too: orders keep the trade they were given, so the filter has to reach them.
  // The form dialog's key, so the two share one request.
  const tradesQuery = useQuery(
    ["cmmsTrades", companyKey, true],
    () => getTrades(companyKey, true),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const tradeFilterOptions = useMemo(
    () => toTradeFilterOptions(tradesQuery.data, (name) => intl.formatMessage(messages.fieldTradeRetired, { name })),
    [tradesQuery.data, intl],
  );

  // The tag each trade's cards wear in the week and the month. Only while the filter is on every
  // trade: with one trade picked, every card would say the same.
  const tradeTagById = useMemo(() => getTradeTags(tradesQuery.data), [tradesQuery.data]);
  const shownTradeTags = tradeFilter === ALL ? tradeTagById : null;

  const deleteMutation = useMutation({ mutationFn: (id) => deleteWorkOrder(companyKey, id) });

  const assetNodeNameById = useMemo(() => {
    const map = new Map();
    flattenAssetNodes(treeQuery.data).forEach((node) => map.set(node.id, node.name));
    return map;
  }, [treeQuery.data]);

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  const queryClient = useQueryClient();

  /*
   * /mine takes a status and nothing else, so while the toggle is on the other three dropdowns are
   * answered here instead of by the API. The cost is nothing: the list is one person's queue.
   */
  const workOrders = useMemo(() => {
    const rows = workOrdersQuery.data ?? [];
    if (!mineOnly) return rows;
    return rows.filter((workOrder) =>
      (typeFilter === ALL || workOrder.workOrderType === typeFilter)
      && (priorityFilter === ALL || workOrder.priority === priorityFilter)
      && (tradeFilter === ALL || workOrder.tradeId === tradeFilter));
  }, [workOrdersQuery.data, mineOnly, typeFilter, priorityFilter, tradeFilter]);

  const isLoading = workOrdersQuery.isLoading;
  const error = workOrdersQuery.error;

  /*
   * The search is the one filter on this page that is not the API's.
   *
   * The four dropdowns above go into the query key and decide what is fetched; this decides
   * what is shown of it. It searches the row as the grid renders it, through the same two maps
   * the grid resolves its asset and assignee columns with, so typing a machine name finds the
   * order even though the row only carries an id.
   */
  const visibleWorkOrders = useMemo(
    () => filterWorkOrders(workOrders, searchText, { assetNodeNameById, userNameById, intl }),
    [workOrders, searchText, assetNodeNameById, userNameById, intl],
  );

  /*
   * The list's rows: the above, narrowed to a from–to range on one of its dates.
   *
   * Only the list has the range. The week and the month already lay the orders out by date, so they
   * keep reading visibleWorkOrders, and a range set on the list waits there for whoever switches
   * back.
   */
  const listWorkOrders = useMemo(
    () => filterWorkOrdersByDate(visibleWorkOrders, dateRange),
    [visibleWorkOrders, dateRange],
  );

  /*
   * The same filters, applied to the week view's projected rows: the orders the schedules have
   * not raised yet. They come from the schedules rather than from the list above, so the API
   * cannot answer the dropdowns for them, and they are filtered here instead. Each filter is
   * checked against the order a projection will become. filterProjectedOccurrences explains
   * status and Mina. The search is the list's own.
   */
  const filterProjected = useCallback(
    (occurrences) => {
      const anyOr = (value) => (value === ALL ? null : value);
      const matching = filterProjectedOccurrences(occurrences, {
        status: anyOr(statusFilter),
        workOrderType: anyOr(typeFilter),
        priority: anyOr(priorityFilter),
        tradeId: anyOr(tradeFilter),
        mineOnly,
        userId: currentUserId,
      });
      return filterWorkOrders(matching, searchText, { assetNodeNameById, userNameById, intl });
    },
    [statusFilter, typeFilter, priorityFilter, tradeFilter, mineOnly, currentUserId, searchText, assetNodeNameById, userNameById, intl],
  );

  // Filters are sent to the API, so an empty result while any is set means "filtered
  // out", not "nothing exists". The two need different empty states.
  const hasActiveFilters = statusFilter !== ALL || typeFilter !== ALL || priorityFilter !== ALL
    || tradeFilter !== ALL || mineOnly || searchText.trim() !== "" || isDateRangeActive(dateRange);

  // Empties the range but keeps which date it was on, so the next range starts from that choice.
  const handleClearFilters = () => {
    setStatusFilter(ALL);
    setTypeFilter(ALL);
    setPriorityFilter(ALL);
    setTradeFilter(ALL);
    setMineOnly(false);
    setSearchText("");
    setDateRange((current) => ({ ...current, from: null, to: null }));
  };

  const handleRowClick = (row) => {
    setSelectedWorkOrderId(row.id);
  };

  /*
   * Closing clears the parameter as well as the state, or the drawer would reopen on every
   * refresh for as long as the link stayed in the address bar. `replace` so the back button steps
   * out of the page rather than back through one drawer open and shut.
   */
  const handleCloseDetails = () => {
    setSelectedWorkOrderId(null);

    if (searchParams.has(WORK_ORDER_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(WORK_ORDER_PARAM);
      setSearchParams(next, { replace: true });
    }
  };

  const handleEdit = (row) => {
    setFormState({ open: true, workOrderId: row.id });
  };

  const handleDelete = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.deleteWorkOrderTitle),
      message: intl.formatMessage(messages.deleteWorkOrderMessage, { title: row.title }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(row.id);
          setConfirmation(null);
          if (selectedWorkOrderId === row.id) {
            setSelectedWorkOrderId(null);
            if (searchParams.has(WORK_ORDER_PARAM)) {
              const next = new URLSearchParams(searchParams);
              next.delete(WORK_ORDER_PARAM);
              setSearchParams(next, { replace: true });
            }
          }
          pushNotification(intl.formatMessage(messages.toastWorkOrderDeleted));
          await workOrdersQuery.refetch();
          // A deleted work order can no longer be mine — keep the sidebar badge honest.
          await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
        } catch (mutationError) {
          pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(messages.loadFailed)));
        }
      },
    });
  };

  const handleAddWorkOrder = () => {
    setFormState({ open: true, workOrderId: null });
  };

  const closeForm = () => {
    setFormState({ open: false, workOrderId: null });
  };

  const handleFormSuccess = async (result) => {
    closeForm();
    await workOrdersQuery.refetch();
    if (result?.id) setSelectedWorkOrderId(result.id);
  };

  return (
    <Box sx={getPageShellSx(cmmsColors)}>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <BuildCircleIcon sx={getPageHeaderIconSx(cmmsColors)} />
            <Box>
              <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
                <FormattedMessage {...cmmsMessages.workOrderTitlePlural} />
              </Typography>
              <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageSubtitle} />
              </Typography>
            </Box>
          </Box>

          {/*
            * Every tile in one container, so the header is two things — the title and the
            * numbers — rather than one thing per tile. As separate children of a space-between
            * row they were spread across the whole width, which put Total adrift in the middle
            * and stretched the priority counts into a slab against the right edge.
            *
            * The three answer three different populations — what is on screen, what is open, and
            * everything raised — so each one's label has to name its own, and none of them may
            * lose its title to save a line.
            */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statTotal} />
              </Typography>
              {/*
                * Counts what is on screen, search included. It already moved with the four
                * dropdowns, so leaving the search out would put a number above a grid that
                * disagrees with it. The same goes for the list's date range, which the week and
                * the month do not apply.
                *
                * mt lines this number up with the priority tile's chips beside it, which sit
                * lower than a bare number would.
                */}
              <Typography variant={cmmsTypeScale.statValue} sx={{ ...getStatValueSx(cmmsColors), mt: 0.5 }}>
                {(isListView ? listWorkOrders : visibleWorkOrders).length}
              </Typography>
            </Box>

            {/* Both read the unfiltered list, for the reason spelled out at allWorkOrdersQuery. */}
            <WorkOrderPriorityTile workOrders={allWorkOrdersQuery.data} />
            <WorkOrderTypeTile workOrders={allWorkOrdersQuery.data} />
          </Box>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mt: 2, pt: 2, borderTop: `1px solid ${cmmsColors.border}` }}>
          {/*
            * The module's own button styling, not MUI's default. palette.primary.main is
            * colors.primary[500] — the page background — so a bare contained button paints
            * itself the colour of what is behind it and reads as a ghost. getContainedButtonSx
            * puts the accent green on it, which is what the asset hierarchy's buttons already do.
            */}
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddWorkOrder}
              sx={getContainedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...messages.addWorkOrder} />
            </Button>
          </HideIfNoRole>

          {/*
            * Controlled, so Clear filters can empty it — otherwise the button leaves the list
            * looking filtered with the reason still in the field. Wide enough for its own label:
            * MUI ellipsis-clips an outlined label that outgrows the notch.
            */}
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 420 } }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            label={intl.formatMessage(messages.filterSearch)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="wo-status-filter" sx={getInputLabelSx(cmmsColors)}><FormattedMessage {...messages.fieldStatus} /></InputLabel>
            <Select
              labelId="wo-status-filter"
              label={intl.formatMessage(messages.fieldStatus)}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              <MenuItem value={ALL}><FormattedMessage {...messages.filterAll} /></MenuItem>
              {getWorkOrderStatusOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="wo-type-filter" sx={getInputLabelSx(cmmsColors)}><FormattedMessage {...messages.fieldType} /></InputLabel>
            <Select
              labelId="wo-type-filter"
              label={intl.formatMessage(messages.fieldType)}
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              <MenuItem value={ALL}><FormattedMessage {...messages.filterAll} /></MenuItem>
              {getWorkOrderTypeOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="wo-priority-filter" sx={getInputLabelSx(cmmsColors)}><FormattedMessage {...messages.fieldPriority} /></InputLabel>
            <Select
              labelId="wo-priority-filter"
              label={intl.formatMessage(messages.fieldPriority)}
              value={priorityFilter}
              onChange={(event) => setPriorityFilter(event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              <MenuItem value={ALL}><FormattedMessage {...messages.filterAll} /></MenuItem>
              {getWorkOrderPriorityOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*
            * Only once there is a trade to pick. A company that has not set any up would get a
            * dropdown holding nothing but "All". Kept while a trade is picked, so the filter
            * cannot vanish with a selection still applied.
            */}
          {(tradeFilterOptions.length > 0 || tradeFilter !== ALL) &&
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="wo-trade-filter" sx={getInputLabelSx(cmmsColors)}><FormattedMessage {...messages.fieldTrade} /></InputLabel>
              <Select
                labelId="wo-trade-filter"
                label={intl.formatMessage(messages.fieldTrade)}
                value={tradeFilter}
                onChange={(event) => setTradeFilter(event.target.value)}
                sx={getSelectSx(cmmsColors)}
                MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
              >
                <MenuItem value={ALL}><FormattedMessage {...messages.filterAll} /></MenuItem>
                {tradeFilterOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          }

          {/*
            * The destination the sidebar badge has been counting towards. Switched rather than
            * dropped in beside the other three, because it does not narrow the same list — it
            * changes which list is being asked for.
            */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={mineOnly}
                onChange={(event) => setMineOnly(event.target.checked)}
                sx={getSwitchSx(cmmsColors)}
              />
            }
            label={intl.formatMessage(messages.filterMine)}
            sx={{ color: cmmsColors.text, whiteSpace: "nowrap" }}
          />
        </Stack>
      </Box>

      {/*
        * Below the filters rather than among them, because they are not a filter: everything
        * above decides which orders, this decides how they are laid out, and all three views read
        * the same list. The module's tab strip, as on the asset hierarchy and settings pages, with
        * no gap under it so the selected tab joins the tile below.
        */}
      <Tabs
        value={isListView ? WorkOrderView.List : view}
        onChange={(_event, value) => setView(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={intl.formatMessage(messages.viewToggleAria)}
        sx={{ ...getTabsSx(cmmsColors), mt: 2 }}
      >
        <Tab disableRipple value={WorkOrderView.List} label={intl.formatMessage(messages.viewList)} />
        <Tab disableRipple value={WorkOrderView.Week} label={intl.formatMessage(messages.viewWeek)} />
        <Tab disableRipple value={WorkOrderView.Month} label={intl.formatMessage(messages.viewMonth)} />
      </Tabs>

      <Box sx={getSectionBoxSx(cmmsColors)}>
        {error &&
          <CmmsErrorState
            error={error}
            fallback={intl.formatMessage(messages.loadFailed)}
            onRetry={() => workOrdersQuery.refetch()}
            isRetrying={workOrdersQuery.isFetching}
          />
        }
        {/* The same filtered list as the grid, and the same drawer on a click, so switching
            view never changes which orders are on the page or what opening one does. */}
        {!error && isWeekView &&
          <WorkOrderWeekView
            companyKey={companyKey}
            workOrders={visibleWorkOrders}
            isLoading={isLoading}
            assetNodeNameById={assetNodeNameById}
            userNameById={userNameById}
            tradeOptions={tradeFilterOptions}
            tradeTagById={shownTradeTags}
            filterProjected={filterProjected}
            onRowClick={handleRowClick}
          />
        }
        {!error && isMonthView &&
          <WorkOrderMonthView
            companyKey={companyKey}
            workOrders={visibleWorkOrders}
            isLoading={isLoading}
            assetNodeNameById={assetNodeNameById}
            userNameById={userNameById}
            tradeOptions={tradeFilterOptions}
            tradeTagById={shownTradeTags}
            filterProjected={filterProjected}
            onRowClick={handleRowClick}
          />
        }
        {/* The grid renders through loading rather than being swapped for a spinner, so
            the toolbar and the 70vh frame keep their place and the skeleton fills the
            body. Swapping the whole grid out made the page height jump on every filter
            change. */}
        {!error && isListView &&
          <WorkOrdersDataGrid
            workOrders={listWorkOrders}
            // isLoading, not isFetching: the overlay covers the grid whenever `loading`
            // is set, so using isFetching would replace rows the user is reading with a
            // skeleton on every background refetch.
            isLoading={isLoading}
            assetNodeNameById={assetNodeNameById}
            userNameById={userNameById}
            onRowClick={handleRowClick}
            onEdit={handleEdit}
            onDelete={handleDelete}
            // In the toolbar's right-hand corner, on the line with Columns and Filters.
            toolbarEnd={<WorkOrderDateFilter value={dateRange} onChange={setDateRange} />}
            emptyState={
              hasActiveFilters
                ? <CmmsEmptyState
                    icon={<FilterAltOffIcon />}
                    title={intl.formatMessage(messages.emptyFilteredTitle)}
                    hint={intl.formatMessage(messages.emptyFilteredHint)}
                    action={
                      <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                        <FormattedMessage {...messages.filterClear} />
                      </Button>
                    }
                  />
                : <CmmsEmptyState
                    icon={<BuildCircleIcon />}
                    title={intl.formatMessage(messages.emptyTitle)}
                    hint={intl.formatMessage(messages.emptyHint)}
                    action={
                      <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                        {/* The empty state's copy of the Add button, and the same ghost without
                            the module's styling — it is the one button on an otherwise blank
                            page, so it is the worst place to lose it. */}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddWorkOrder}
                          sx={getContainedButtonSx(cmmsColors)}
                        >
                          <FormattedMessage {...messages.addWorkOrder} />
                        </Button>
                      </HideIfNoRole>
                    }
                  />
            }
          />
        }
      </Box>

      <WorkOrderDetailsPanel
        workOrderId={selectedWorkOrderId}
        companyKey={companyKey}
        assetNodeNameById={assetNodeNameById}
        userNameById={userNameById}
        onClose={handleCloseDetails}
        onEdit={(workOrder) => setFormState({ open: true, workOrderId: workOrder.id })}
        onEscalate={(workOrder) => setEscalateState({
          open: true,
          workOrderId: workOrder.id,
          // So the dialog can leave out the department it is already in, which the server refuses.
          currentDepartmentId: workOrder.assignedDepartmentId ?? null,
          // And so it can refuse a hand-over to the trade it already has, with nothing else moving.
          currentTradeId: workOrder.tradeId ?? null,
        })}
        onChanged={() => workOrdersQuery.refetch()}
      />

      <WorkOrderFormDialog
        open={formState.open}
        workOrderId={formState.workOrderId}
        companyKey={companyKey}
        onClose={closeForm}
        onSuccess={handleFormSuccess}
      />

      <EscalateDialog
        open={escalateState.open}
        workOrderId={escalateState.workOrderId}
        companyKey={companyKey}
        currentDepartmentId={escalateState.currentDepartmentId}
        currentTradeId={escalateState.currentTradeId}
        onClose={() => setEscalateState({ open: false, workOrderId: null, currentDepartmentId: null, currentTradeId: null })}
        onSuccess={async (result) => {
          await workOrdersQuery.refetch();
          // The history and the comment thread both gained a row, and the panel is very likely
          // still open on this order — escalation now returns the same order rather than a new one.
          // The panel's own read is stale too: its department, trade, status and assignee all changed.
          await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrder", companyKey, result?.id] });
          await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrderStatusLog", companyKey, result?.id] });
          await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrderComments", companyKey, result?.id] });
          // It has left the escalating technician's queue and entered the receiving department's or trade's.
          await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
          if (result?.id) setSelectedWorkOrderId(result.id);
        }}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? "Yes"}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={deleteMutation.isPending || deleteMutation.isLoading} />
    </Box>
  );
}
